import { logger } from '../../../services/logger';
import { useState, useEffect, useMemo } from 'react';
import {
    fetchAllProgramSnapshots,
    transformSeasonalPrograms,
    transformCampPrograms,
    transformRacePrograms,
    transformEventPrograms,
    buildProgramsSubmenu,
    buildCampsAndRacesSubmenu,
    isNavSubItemVisible
} from './useNavbarNavigationHelpers';

export const navItemsInitial = [
    {
        label: 'Programs & Events',
        path: '/programs',
        submenu: [], // Will be populated dynamically
    },
    {
        label: 'Camps & Races',
        path: '/races',
        submenu: [],
    },
    {
        label: 'Calendar',
        path: '/calendar',
    },
    {
        label: 'About',
        path: '/about',
        submenu: [
            { label: 'About', path: '/about' },
            { label: 'Fitness', path: '/fitness' },
            { label: 'Sponsors', path: '/sponsors' },
            { label: 'Coaches', path: '/staff' },
            { label: 'Athletes', path: '/athletes', requireCommunity: true },
            { label: 'Alumni', path: '/alumni', requireCommunity: true },
            { label: 'USSS Points Guide', path: '/points' },
            { label: 'Scholarships', path: '/scholarships' },
            { label: 'SafeSport', path: '/safesport' },
            { label: 'Transportation', path: '/transportation' },
            { label: 'Lodging', path: '/lodging' },
            { label: 'Equipment Recommendations', path: '/equipment-recommendations' },
            { label: 'Contact', path: '/contact' },
        ],
    },
];

export function useNavbarNavigation({ user, isAdmin, isProgramsManager, isCommunityMember, registeredProgramIds }) {
    const [navItems, setNavItems] = useState(navItemsInitial);

    useEffect(() => {
        const fetchNavigationData = async () => {
            const canViewPreview = Boolean(isAdmin || isProgramsManager);
            try {
                const snapshots = await fetchAllProgramSnapshots(canViewPreview);
                const { seasonalSnapshot, campsSnapshot, racesSnapshot, eventsSnapshot, previewSnapshot } = snapshots;

                const seasonalPrograms = transformSeasonalPrograms(seasonalSnapshot, previewSnapshot, canViewPreview);
                const campPrograms = transformCampPrograms(campsSnapshot, previewSnapshot, canViewPreview);
                const activeRaces = transformRacePrograms(racesSnapshot, previewSnapshot, canViewPreview);
                const eventPrograms = transformEventPrograms(eventsSnapshot, previewSnapshot, canViewPreview);

                setNavItems(prevItems => {
                    const newItems = [...prevItems];

                    const programsIndex = newItems.findIndex(item => item.label === 'Programs & Events' || item.label === 'Programs');
                    if (programsIndex !== -1) {
                        newItems[programsIndex] = {
                            ...newItems[programsIndex],
                            submenu: buildProgramsSubmenu(seasonalPrograms, eventPrograms)
                        };
                    }

                    const racesIndex = newItems.findIndex(item => item.label === 'Camps & Races' || item.label === 'Races');
                    if (racesIndex !== -1) {
                        newItems[racesIndex] = {
                            ...newItems[racesIndex],
                            submenu: buildCampsAndRacesSubmenu(campPrograms, activeRaces)
                        };
                    }

                    return newItems;
                });
            } catch (error) {
                logger.warn('Error fetching navigation data:', error);
            }
        };

        fetchNavigationData();
    }, [isAdmin, isProgramsManager]);

    const baseNavItems = useMemo(() => {
        return navItems;
    }, [navItems]);

    const filteredNavItems = useMemo(() => {
        const filterContext = { user, isCommunityMember, isAdmin, registeredProgramIds };
        return baseNavItems.map(item => {
            if (item.submenu) {
                return {
                    ...item,
                    submenu: item.submenu.filter(subItem => isNavSubItemVisible(subItem, filterContext))
                };
            }
            return item;
        }).filter(Boolean);
    }, [baseNavItems, user, isCommunityMember, isAdmin, registeredProgramIds]);

    const isItemRegistered = (subItem) => {
        if (!subItem?.id && !subItem?.link) return false;
        const matchesId = Boolean(subItem.id && registeredProgramIds?.has(subItem.id));
        const matchesLink = Boolean(subItem.link && registeredProgramIds?.has(subItem.link));
        const matchesVersionOf = Boolean(subItem.versionOf && registeredProgramIds?.has(subItem.versionOf));
        return matchesId || matchesLink || matchesVersionOf;
    };

    return {
        navItems,
        filteredNavItems,
        isItemRegistered
    };
}
